import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * Offers of the organization, Matrix-Authz only (see feature page angebote).
 * `number` is the internal counter (routes, paths); `offerNumber` is the number on the document.
 */

/**
 * State
 */
const defaultOffer = () => {
  return {
    _id: '_new',
    number: '_new',
    offerType: 'OUTGOING',
    editingState: 'DRAFT',
    customerState: 'OPEN',
    offerNumber: '',
    netAmount: null,
    vatPercentage: 19,
    taxAmount: null,
    grossAmount: null,
    offerDate: null,
    validUntil: null,
    project: null,
    supplier: null,
    notes: '',
    attachments: [],
    createdBy: null,
  };
};

const getDefaultState = () => {
  return {
    offers: [],
    offerCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an offer list view
 * used for paginated offer queries
 * @type {DocumentNode}
 */
const OFFER_LIST_REQUEST_FIELDS = gql`
  fragment offerListFields on Offer {
    _id
    number
    offerType
    editingState
    customerState
    offerNumber
    netAmount
    grossAmount
    offerDate
    validUntil
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * Defines the fields which are requested for an offer
 * used for all queries/mutations which return an offer
 * @type {DocumentNode}
 */
const OFFER_REQUEST_FIELDS = gql`
  fragment offerFields on Offer {
    _id
    number
    offerType
    editingState
    customerState
    offerNumber
    netAmount
    vatPercentage
    taxAmount
    grossAmount
    offerDate
    validUntil
    notes
    attachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
      attachmentType
    }
    attachmentTypes
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        city
        zip
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api; the server calculates tax and
 * gross (taxAmount only counts in manual mode). A cleared supplier is sent as null.
 * @param {*} offer
 */
const createPayload = (offer) => ({
  offerType: offer.offerType,
  editingState: offer.editingState,
  customerState: offer.customerState,
  offerNumber: offer.offerNumber,
  netAmount: offer.netAmount,
  vatPercentage: offer.vatPercentage,
  taxAmount: offer.taxAmount,
  offerDate: offer.offerDate,
  validUntil: offer.validUntil,
  project: offer.project ? offer.project.id || offer.project._id || offer.project : null,
  supplier: offer.supplier ? offer.supplier._id || offer.supplier : null,
  notes: offer.notes,
});

/**
 * Payload of the block project-offers (`ProjectOfferInput`): without project, the server sets it.
 * @param {*} offer
 */
const createProjectOfferPayload = (offer) => {
  const payload = createPayload(offer);
  delete payload.project;
  return payload;
};

const OFFER_LIST_QUERY = gql`
  query offersPaginated(
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: OfferFilterInput
  ) {
    offersPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
      offers {
        ...offerListFields
      }
      offerCount
    }
  }
  ${OFFER_LIST_REQUEST_FIELDS}
`;

/** Offer list of the block project-offers; the server restricts it to the project. */
const PROJECT_OFFER_LIST_QUERY = gql`
  query projectOffers(
    $projectNumber: Int!
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: OfferFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        offers {
          id
          number
          offers(pagination: $pagination, sorting: $sorting, filters: $filters) {
            offers {
              ...offerListFields
            }
            offerCount
          }
        }
      }
    }
  }
  ${OFFER_LIST_REQUEST_FIELDS}
`;

/**
 * Request of the offer list: with a project number the block project-offers, without it the
 * global query offersPaginated. `pagesOf` returns the `OfferPages` of the response.
 */
const offerListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_OFFER_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.offers.offers,
    };
  }
  return { query: OFFER_LIST_QUERY, pagesOf: (data) => data.offersPaginated };
};

const OFFER_BY_NUMBER_QUERY = gql`
  query offerByNumber($offerNumber: Int!) {
    offerByNumber(offerNumber: $offerNumber) {
      ...offerFields
    }
  }
  ${OFFER_REQUEST_FIELDS}
`;

/** One offer of the block project-offers; an offer of another project is not found. */
const PROJECT_OFFER_QUERY = gql`
  query projectOffer($projectNumber: Int!, $offerNumber: Int!) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        offers {
          id
          number
          offer(offerNumber: $offerNumber) {
            ...offerFields
          }
        }
      }
    }
  }
  ${OFFER_REQUEST_FIELDS}
`;

/**
 * Request of one offer: with a project number the block project-offers, without it the global
 * query offerByNumber. `offerOf` returns the `Offer` of the response.
 */
const offerRequest = (offerNumber, projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_OFFER_QUERY,
      variables: { projectNumber, offerNumber },
      offerOf: (data) => data.projectByNumber.blocks.offers.offer,
    };
  }
  return {
    query: OFFER_BY_NUMBER_QUERY,
    variables: { offerNumber },
    offerOf: (data) => data.offerByNumber,
  };
};

/**
 * Request of a create: with a project number the mutation of the block project-offers (payload
 * without project), without it the global mutation.
 */
const createOfferRequest = (offer, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectOffer($projectNumber: Int!, $payload: ProjectOfferInput!) {
          createProjectOffer(projectNumber: $projectNumber, offerInput: $payload) {
            ...offerFields
          }
        }
        ${OFFER_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectOfferPayload(offer) },
      resultField: 'createProjectOffer',
    };
  }
  return {
    mutation: gql`
      mutation createOffer($payload: OfferInput!) {
        createOffer(offerInput: $payload) {
          ...offerFields
        }
      }
      ${OFFER_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(offer) },
    resultField: 'createOffer',
  };
};

/** Request of an update, chosen by the project number as `createOfferRequest`. */
const updateOfferRequest = (offer, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectOffer(
          $projectNumber: Int!
          $offerNumber: Int!
          $payload: ProjectOfferInput!
        ) {
          updateProjectOffer(
            projectNumber: $projectNumber
            offerNumber: $offerNumber
            offerInput: $payload
          ) {
            ...offerFields
          }
        }
        ${OFFER_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        offerNumber: offer.number,
        payload: createProjectOfferPayload(offer),
      },
      resultField: 'updateProjectOffer',
    };
  }
  return {
    mutation: gql`
      mutation updateOffer($offerNumber: Int!, $payload: OfferInput!) {
        updateOffer(offerNumber: $offerNumber, offerInput: $payload) {
          ...offerFields
        }
      }
      ${OFFER_REQUEST_FIELDS}
    `,
    variables: { offerNumber: offer.number, payload: createPayload(offer) },
    resultField: 'updateOffer',
  };
};

/**
 * Request of an attachment upload: with a project number the mutation of the block (the server
 * sets the target offer of the project), without it the global mutation.
 */
const uploadOfferAttachmentRequest = ({
  file,
  parentId,
  parentType,
  offerNumber,
  projectNumber,
}) => {
  const attachmentInput = { referenceId: parentId, referenceType: parentType, file };
  if (projectNumber) {
    return {
      mutation: gql`
        mutation uploadProjectOfferAttachment(
          $projectNumber: Int!
          $offerNumber: Int!
          $attachmentInput: AttachmentWithTypeInput!
        ) {
          uploadProjectOfferAttachment(
            projectNumber: $projectNumber
            offerNumber: $offerNumber
            attachmentInput: $attachmentInput
          ) {
            ...offerFields
          }
        }
        ${OFFER_REQUEST_FIELDS}
      `,
      variables: { projectNumber, offerNumber, attachmentInput },
      resultField: 'uploadProjectOfferAttachment',
    };
  }
  return {
    mutation: gql`
      mutation uploadOfferAttachment($attachmentInput: AttachmentWithTypeInput) {
        uploadOfferAttachment(attachmentInput: $attachmentInput) {
          ...offerFields
        }
      }
      ${OFFER_REQUEST_FIELDS}
    `,
    variables: { attachmentInput },
    resultField: 'uploadOfferAttachment',
  };
};

/** Request of an attachment delete, chosen by the project number as the upload. */
const deleteOfferAttachmentRequest = ({ offerId, offerNumber, attachmentId, projectNumber }) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectOfferAttachment(
          $projectNumber: Int!
          $offerNumber: Int!
          $attachmentId: ID!
        ) {
          deleteProjectOfferAttachment(
            projectNumber: $projectNumber
            offerNumber: $offerNumber
            attachmentId: $attachmentId
          ) {
            ...offerFields
          }
        }
        ${OFFER_REQUEST_FIELDS}
      `,
      variables: { projectNumber, offerNumber, attachmentId },
      resultField: 'deleteProjectOfferAttachment',
    };
  }
  return {
    mutation: gql`
      mutation deleteOfferAttachment($offerId: ID!, $attachmentId: ID!) {
        deleteOfferAttachment(offerId: $offerId, attachmentId: $attachmentId) {
          ...offerFields
        }
      }
      ${OFFER_REQUEST_FIELDS}
    `,
    variables: { offerId, attachmentId },
    resultField: 'deleteOfferAttachment',
  };
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered offer list; with `projectNumber` through the block
   * project-offers.
   * @param {*} param0
   * @param {*} payload
   */
  async fetchOffersPaginated({ commit }, { pagination, sorting, filters, projectNumber }) {
    const { query, pagesOf } = offerListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const offerPages = pagesOf(response.data);
      commit('SET_OFFERS', offerPages.offers);
      commit('SET_OFFER_COUNT', offerPages.offerCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /** Loads one offer; with `projectNumber` through the block project-offers. */
  async fetchOfferByNumber({ commit }, { offerNumber, projectNumber }) {
    try {
      const { query, variables, offerOf } = offerRequest(parseInt(offerNumber), projectNumber);
      const response = await apolloClient.query({ query, variables });
      commit('ADD_OR_UPDATE_OFFER', offerOf(response.data));
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Creates the offer; with `projectNumber` through the block project-offers. */
  async createOffer({ commit, dispatch }, { offer, projectNumber }) {
    const { mutation, variables, resultField } = createOfferRequest(offer, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Angebot gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_OFFER', '_new'); // remove temporary _new offer
      commit('ADD_OR_UPDATE_OFFER', response.data[resultField]);
      return response.data[resultField].number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Updates the offer; with `projectNumber` through the block project-offers. */
  async updateOffer({ commit, dispatch }, { offer, projectNumber }) {
    const { mutation, variables, resultField } = updateOfferRequest(offer, projectNumber);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Angebot aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_OFFER', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteOffer({ commit, dispatch }, offerNumber) {
    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation deleteOffer($offerNumber: Int!) {
            deleteOffer(offerNumber: $offerNumber)
          }
        `,
        variables: {
          offerNumber: offerNumber,
        },
      });
      dispatch('message', {
        message: 'Angebot gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_OFFER', offerNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new offer object in store
   * @param {*} param0
   */
  async initOffer({ commit }) {
    commit('ADD_OR_UPDATE_OFFER', defaultOffer());
  },

  offerLocalUpdate({ commit }, offer) {
    commit('ADD_OR_UPDATE_OFFER', offer);
  },

  offerCleanup({ commit }) {
    commit('REMOVE_OFFER', '_new');
  },

  /**
   * Upload an attachment to an offer
   * @param {*} param0
   * @param {*} payload - contains parentId (offer id), parentType (attachment type), file and, in
   * the block, offerNumber and projectNumber
   */
  async uploadOfferAttachment({ commit, dispatch }, payload) {
    const { mutation, variables, resultField } = uploadOfferAttachmentRequest(payload);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('ADD_OR_UPDATE_OFFER', response.data[resultField]);
      dispatch('message', {
        message: 'Anhang hochgeladen!',
        type: 'success',
        lifetime: 2,
      });
      return response.data[resultField];
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim hochladen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete an attachment from an offer
   * @param {*} param0
   * @param {*} payload - contains offerId, attachmentId and, in the block, offerNumber and
   * projectNumber
   */
  async deleteOfferAttachment({ commit, dispatch }, payload) {
    const { mutation, variables, resultField } = deleteOfferAttachmentRequest(payload);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('ADD_OR_UPDATE_OFFER', response.data[resultField]);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_OFFERS: (state, offers) => (offers ? (state.offers = offers) : (state.offers = [])),
  ADD_OR_UPDATE_OFFER: (state, updatedOffer) => {
    const existingOffer = state.offers.find((offer) => offer._id === updatedOffer._id);
    if (existingOffer) {
      Object.assign(existingOffer, updatedOffer);
    } else {
      state.offers.push(updatedOffer);
    }
  },
  REMOVE_OFFER: (state, offerId) => {
    const index = state.offers.findIndex(
      (offer) => offer._id === offerId || offer.number === offerId
    );
    if (index !== -1) {
      state.offers.splice(index, 1);
    }
  },
  SET_OFFER_COUNT: (state, offerCount) => (state.offerCount = offerCount),
};

/**
 * Getters
 */
const getters = {
  getOffers: (state) => state.offers,
  getOfferByNumber: (state) => (offerNumber) =>
    state.offers.find((offer) => offer.number == offerNumber),
  getOfferCount: (state) => state.offerCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
